import type { RadioProps } from '@byecode/ui'
import { Checkbox, Radio } from '@byecode/ui'
import type { Field } from '@lighthouse/core'
import { camelToKebab } from '@lighthouse/tools'
import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'

import { useRecordData } from './useRecordData'

interface CheckboxItemWrapperProps {
    recordId: string
    dsId: string
    relativeField: Field | undefined
    relativeShowField: Field | undefined
    value: string[]
    isMultiple?: boolean
    disabled?: boolean
    onChange: (newValue: string[]) => void
    // 样式相关
    radioHoverStyle?: Record<'input' | 'label', React.CSSProperties>
    checkboxHoverStyle?: Record<'inner' | 'label', React.CSSProperties>
    radioStyle: (checked: boolean) => RadioProps['styles']
    checkboxStyle: (checked: boolean) => Record<string, React.CSSProperties>
    activeCheckedColorStyle?: string
}

const SCxGroup = styled.div<{
    radioHoverStyle?: Record<'input' | 'label', React.CSSProperties>
    checkboxHoverStyle?: Record<'inner' | 'label', React.CSSProperties>
}>`
    display: flex;
    gap: 12px;

    ${({ radioHoverStyle }) =>
        radioHoverStyle &&
        css`
            & .byecode-radio-input:hover {
                ${Object.entries(radioHoverStyle.input)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
            & .byecode-radio-label:hover {
                ${Object.entries(radioHoverStyle.label)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}

    ${({ checkboxHoverStyle }) =>
        checkboxHoverStyle &&
        css`
            & .byecode-checkbox-inner:hover {
                ${Object.entries(checkboxHoverStyle.inner)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
            & .byecode-checkbox-label:hover {
                ${Object.entries(checkboxHoverStyle.label)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}
`

export const CheckboxItemWrapper: React.FC<CheckboxItemWrapperProps> = ({
    recordId,
    dsId,
    relativeField,
    relativeShowField,
    value,
    isMultiple,
    disabled,
    onChange,
    radioHoverStyle,
    checkboxHoverStyle,
    radioStyle,
    checkboxStyle,
    activeCheckedColorStyle
}) => {
    // 使用 useRecordData 订阅 record 数据
    const { displayValue, fieldRawValue, disabled: recordDisabled } = useRecordData(recordId, dsId, relativeField, relativeShowField)

    const checked = useMemo(() => Boolean(value?.includes(recordId)), [value, recordId])

    const handleChange = () => {
        if (recordDisabled || disabled) {
            return
        }
        const newValue = checked ? value?.filter(v => v !== recordId) : [...value, recordId]
        onChange(newValue)
    }

    if (!displayValue) {
        return null
    }

    return (
        <SCxGroup radioHoverStyle={radioHoverStyle} checkboxHoverStyle={checkboxHoverStyle} onMouseDown={handleChange}>
            {isMultiple ? (
                <Checkbox
                    checked={checked}
                    color={activeCheckedColorStyle}
                    disabled={recordDisabled || disabled}
                    styles={checkboxStyle(checked)}
                    size="xs"
                    value={fieldRawValue}
                    label={displayValue}
                />
            ) : (
                <Radio
                    size="xs"
                    styles={radioStyle(checked)}
                    label={displayValue}
                    disabled={recordDisabled || disabled}
                    color={checked ? activeCheckedColorStyle : ''}
                    checked={checked}
                />
            )}
        </SCxGroup>
    )
}
